import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Globe, Lock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useAllowedDomains } from "@/hooks/useDomainQuery";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { EmbedBuilder } from "@/components/embed/EmbedBuilder";

export const Route = createFileRoute("/_layout/embed")({
	component: EmbedPage,
	head: () => ({
		meta: [
			{
				title: "Embed Macrowave - Add Live Streaming to Any Website",
			},
			{
				name: "description",
				content:
					"Add live radio to your website with two lines of code. Works on WordPress, Wix, Squarespace, and more.",
			},
			{
				property: "og:title",
				content: "Embed Macrowave - Add Live Streaming to Any Website",
			},
			{
				property: "og:description",
				content:
					"Add live radio to your website with two lines of code. Works on WordPress, Wix, Squarespace, and more.",
			},
		],
	}),
});

function EmbedPage() {
	const { user, isAuthenticated, signInWithApple } = useAuth();
	const { data: domains } = useAllowedDomains();

	// Query user's stations for preview
	const { data: userStations } = useQuery({
		queryKey: ["user-stations", user?.username],
		queryFn: async () => {
			if (!user?.username) return null;
			return apiClient.getUserStations(user.username);
		},
		enabled: !!user?.username,
	});

	const firstStation = userStations?.[0];

	return (
		<div className="mx-auto max-w-4xl px-6 py-12">
			{/* Hero Section */}
			<div className="mb-20 text-center">
				<h1 className="mb-6 font-serif-600 text-6xl text-black leading-tight">
					Embed Live Streaming
				</h1>
				<p className="font-serif text-2xl text-gray-600 leading-relaxed">
					Add your radio to any website. Two lines of code.
				</p>
			</div>

			{/* Embed Builder */}
			<div className="mb-20">
				<h2 className="mb-8 font-serif-600 text-4xl text-black">Try It Out</h2>
				<p className="mb-8 font-serif text-lg text-gray-600 leading-relaxed">
					Configure your embed and see how it looks. Choose between script tag
					(recommended) or iframe embedding, customize the theme and size, then
					copy the code.
				</p>
				{!isAuthenticated && (
					<div className="mb-8 rounded-2xl border border-blue-200 bg-blue-50 p-8 shadow-sm">
						<p className="mb-4 font-serif text-lg text-gray-800">
							Sign in to get your personalized embed code and manage domain
							whitelisting.
						</p>
						<button
							onClick={() => signInWithApple("/embed")}
							className="rounded-lg bg-black px-6 py-3 font-serif text-white transition-all hover:bg-gray-800 hover:shadow-md"
						>
							Sign In with Apple
						</button>
					</div>
				)}
				<EmbedBuilder
					station={
						isAuthenticated && firstStation
							? `${user.username}/${firstStation.slug}`
							: undefined
					}
				/>
			</div>

			{/* How It Works */}
			<div className="mb-20">
				<h2 className="mb-8 font-serif-600 text-4xl text-black">
					How It Works
				</h2>
				<div className="space-y-8 font-serif text-lg leading-relaxed text-gray-800">
					<div className="flex gap-6">
						<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-black font-serif-600 text-xl text-white">
							1
						</div>
						<div>
							<h3 className="mb-2 font-serif-600 text-xl text-black">
								Copy the embed code
							</h3>
							<p className="text-gray-600 leading-relaxed">
								Get your personalized embed code above or use the generic
								version.
							</p>
						</div>
					</div>
					<div className="flex gap-6">
						<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-black font-serif-600 text-xl text-white">
							2
						</div>
						<div>
							<h3 className="mb-2 font-serif-600 text-xl text-black">
								Paste it on your website
							</h3>
							<p className="text-gray-600 leading-relaxed">
								Add the code to any HTML page, WordPress site, or website
								builder.
							</p>
						</div>
					</div>
					<div className="flex gap-6">
						<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-black font-serif-600 text-xl text-white">
							3
						</div>
						<div>
							<h3 className="mb-2 font-serif-600 text-xl text-black">
								Whitelist your domain (optional)
							</h3>
							<p className="text-gray-600 leading-relaxed">
								For added security, whitelist the domains where your player will
								appear.
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Domain Whitelist Section */}
			<div className="mb-20">
				<h2 className="mb-8 font-serif-600 text-4xl text-black">
					Domain Whitelisting
				</h2>
				{isAuthenticated ? (
					<div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
						<div className="mb-4 flex items-start gap-4">
							<CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-green-600" />
							<div>
								<h3 className="mb-3 font-serif-600 text-2xl text-black">
									You're all set
								</h3>
								<p className="mb-6 font-serif text-lg text-gray-600">
									{domains && domains.length > 0
										? `You have ${domains.length} whitelisted domain${domains.length > 1 ? "s" : ""}.`
										: "You haven't whitelisted any domains yet."}
								</p>
								{domains && domains.length > 0 && (
									<div className="mb-6 space-y-3">
										{domains.map((domain) => (
											<div
												key={domain.id}
												className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 font-mono text-sm"
											>
												<Globe className="h-5 w-5 text-gray-500" />
												<span className="text-gray-800">{domain.domain}</span>
											</div>
										))}
									</div>
								)}
								<Link
									to="/settings/domains"
									className="inline-flex items-center gap-2 font-serif text-lg text-blue-600 hover:text-blue-700"
								>
									Manage Domains →
								</Link>
							</div>
						</div>
					</div>
				) : (
					<div className="rounded-2xl border border-gray-200 bg-gray-50 p-8 shadow-sm">
						<div className="flex items-start gap-4">
							<Lock className="mt-1 h-6 w-6 shrink-0 text-gray-500" />
							<div>
								<h3 className="mb-3 font-serif-600 text-2xl text-black">
									Protect Your Embed
								</h3>
								<p className="mb-6 font-serif text-lg text-gray-600 leading-relaxed">
									Control where your player can be embedded by whitelisting
									specific domains. This prevents unauthorized use of your embed
									code.
								</p>
								<button
									onClick={() => signInWithApple("/embed")}
									className="font-serif text-lg text-blue-600 hover:text-blue-700"
								>
									Sign in to manage domains →
								</button>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Tutorials Section */}
			<div className="mb-20">
				<h2 className="mb-8 font-serif-600 text-4xl text-black">
					Platform Guides
				</h2>
				<p className="mb-8 font-serif text-xl text-gray-600">
					Step-by-step instructions for your platform.
				</p>
				<div className="grid gap-6 sm:grid-cols-2">
					<Link
						to="/tutorials/wordpress-radio-broadcaster"
						className="group rounded-2xl border-2 border-gray-200 p-8 transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
					>
						<h3 className="mb-3 font-serif-600 text-2xl text-black group-hover:text-blue-600">
							WordPress
						</h3>
						<p className="font-serif text-lg text-gray-600">
							Add Macrowave to any WordPress site. Works with all themes.
						</p>
					</Link>
					<Link
						to="/tutorials/wix-radio-broadcaster"
						className="group rounded-2xl border-2 border-gray-200 p-8 transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
					>
						<h3 className="mb-3 font-serif-600 text-2xl text-black group-hover:text-blue-600">
							Wix
						</h3>
						<p className="font-serif text-lg text-gray-600">
							Add Macrowave to Wix. Drag-and-drop embed. No coding required.
						</p>
					</Link>
					<Link
						to="/tutorials/squarespace-radio-broadcaster"
						className="group rounded-2xl border-2 border-gray-200 p-8 transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
					>
						<h3 className="mb-3 font-serif-600 text-2xl text-black group-hover:text-blue-600">
							Squarespace
						</h3>
						<p className="font-serif text-lg text-gray-600">
							Add Macrowave to Squarespace using Code Blocks.
						</p>
					</Link>
					<Link
						to="/tutorials/webflow-radio-broadcaster"
						className="group rounded-2xl border-2 border-gray-200 p-8 transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
					>
						<h3 className="mb-3 font-serif-600 text-2xl text-black group-hover:text-blue-600">
							Webflow
						</h3>
						<p className="font-serif text-lg text-gray-600">
							Add Macrowave to Webflow with custom code embeds.
						</p>
					</Link>
				</div>
				<div className="mt-8 text-center">
					<Link
						to="/tutorials"
						className="font-serif text-xl text-blue-600 hover:text-blue-700"
					>
						View all tutorials →
					</Link>
				</div>
			</div>
		</div>
	);
}
